import { customToFixed } from '@/utils';

import type {
  ICalcResult,
  IGetRateForm,
  IGetRateItem,
  IQuoteSummary,
} from '@features/Rate/types/GetRateTypes';
import type { IItemInput } from '@/features/Trailer3D/types/Trailer3D.types';
import { FREIGHT_CLASS_CONFIG } from '@features/Rate/helpers/constants';

const TRAILER_WIDTH_INCHES = 96;

export const calculateCubicPerPiece = (item: IGetRateItem): number => {
  if (item.length && item.width && item.height) {
    return customToFixed((item.length * item.width * item.height) / 1728);
  }
  return 0;
};

export const calculateTotalCubic = (item: IGetRateItem): number => {
  return calculateCubicPerPiece(item) * Number(item.quantity);
};

export const calculateDensity = (item: IGetRateItem): number => {
  const totalCubic = calculateTotalCubic(item);
  if (totalCubic > 0 && Number(item.weight) > 0) {
    return item.weight / totalCubic;
  }
  return 0;
};

export const calculateFreightClass = (density: number): number | undefined => {
  if (density <= 0) return undefined;

  for (const config of FREIGHT_CLASS_CONFIG) {
    const matchesMin = density >= config.min_weight;
    const matchesMax = config.max_weight === null || density <= config.max_weight;

    if (matchesMin && matchesMax) {
      return config.freight_class_code;
    }
  }

  return 500;
};

export const calculateLinearFeet = (form: IGetRateForm): number => {
  return form.items.reduce((sum, item) => {
    if (Number(item.length) > 0 && Number(item.quantity) > 0) {
      return sum + (Number(item.length) / 12) * Number(item.quantity);
    }
    return sum;
  }, 0);
};

export const calculateQuoteSummary = (form: IGetRateForm): IQuoteSummary => {
  const totalItems = form.items.reduce((sum, item) => sum + Number(item.quantity), 0);
  const totalWeight = form.items.reduce((sum, item) => sum + Number(item.weight), 0);
  const totalCubic = form.items.reduce((sum, item) => sum + calculateTotalCubic(item), 0);
  const totalLinearFeet = calculateLinearFeet(form);
  const itemsWithDensity = form.items.filter((item) => calculateDensity(item) > 0);
  const averageDensity =
    itemsWithDensity.length > 0
      ? itemsWithDensity.reduce((sum, item) => sum + calculateDensity(item), 0) /
        itemsWithDensity.length
      : 0;

  const shipmentTypeMap = {
    dry: 'Dry Product',
    protect: 'PROTECT : 42F TO 81F',
    chilled: 'CHILLED : 28F to 41F',
    frozen: 'FROZEN: -10F TO 27F',
  };

  const ratingTypeMap = {
    handling_units: 'Handling Units',
    linear_foot: 'Linear Foot',
    freight_class: 'Freight Class',
  };

  return {
    totalItems,
    totalWeight,
    totalCubic: Math.round(totalCubic * 100) / 100,
    totalLinearFeet: Math.round(totalLinearFeet * 100) / 100,
    averageDensity: Math.round(averageDensity * 100) / 100,
    shipmentType: shipmentTypeMap[form.shipmentType],
    ratingType: ratingTypeMap[form.ratingType],
  };
};

export function calculateLinearFeetWithDetails(items: IItemInput[]): ICalcResult {
  const floorItems: { l: number; w: number }[] = [];

  // 1. APLANAR Y PREPARAR DATOS
  // Convertimos "Quantity: 3" en 3 objetos individuales y normalizamos números
  items.forEach((item) => {
    const quantity = Number(item.Quantity);
    const length = Number(item.Length);
    const width = Number(item.Width);
    const isTurnable = item.Turnable;

    // Lógica de Stackable (Simplificada):
    // Si "Stackable" es un número y > 1, dividimos la cantidad de items de piso.
    // Si es 1 o false, ocupan espacio de piso individualmente.
    // Para este ejemplo, asumimos Stackable: 1 como "No apilable encima de otro" (Ocupación de suelo total)
    const stackCount = typeof item.Stackable === 'number' ? item.Stackable : 1;
    const floorSpots = Math.ceil(quantity / stackCount);

    for (let i = 0; i < floorSpots; i++) {
      let finalL = length;
      let finalW = width;

      // 2. LÓGICA DE ROTACIÓN (CRUCIAL PARA TU CASO)
      // Si es girable y el ancho excede el camión, O si girarlo hace que ocupe menos "ancho de carril"
      // En tu caso: W=100 > Trailer=96, por lo tanto FORZAMOS el giro.
      if (isTurnable) {
        if (finalW > TRAILER_WIDTH_INCHES) {
          // Girar obligatoriamente porque no cabe
          [finalL, finalW] = [finalW, finalL];
        }
        // Opcional: Lógica de optimización (girar si el largo es menor que el ancho para aprovechar espacio lateral)
        // Para tu resultado exacto de 8.33, el sistema giró: 10x100 -> 100x10
      }

      floorItems.push({ l: finalL, w: finalW });
    }
  });

  // 3. ORDENAR ITEMS (Opcional pero recomendado: Mayor longitud primero)
  // Esto ayuda a optimizar el tetris, aunque para items idénticos no afecta.
  floorItems.sort((a, b) => b.l - a.l);

  // 4. ALGORITMO DE LLENADO DE FILAS (Row Packing)
  let totalLinearInches = 0;
  let currentRowWidth = 0;
  let currentRowMaxLength = 0;

  for (const item of floorItems) {
    // Si el item cabe en la fila actual
    if (currentRowWidth + item.w <= TRAILER_WIDTH_INCHES) {
      currentRowWidth += item.w;
      // El largo de la fila lo dicta el objeto más largo en esa fila
      currentRowMaxLength = Math.max(currentRowMaxLength, item.l);
    } else {
      // No cabe, cerrar fila actual y sumar su largo al total
      totalLinearInches += currentRowMaxLength;

      // Iniciar nueva fila con el item actual
      currentRowWidth = item.w;
      currentRowMaxLength = item.l;
    }
  }

  // Sumar la última fila pendiente
  if (currentRowMaxLength > 0) {
    totalLinearInches += currentRowMaxLength;
  }

  // 5. RESULTADO FINAL
  return {
    linearFeet: totalLinearInches / 12,
    linearInches: totalLinearInches,
    details: `Calculado basado en ${floorItems.length} bultos de piso.`,
  };
}
